import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, AlertTriangle } from "lucide-react";
import { api, errMsg, downloadFile } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { SignaturePad } from "./SignaturePad";

function TemplatePicker({ tpls, sel, toggle }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="kit-template-list">
      {tpls.map((t) => (
        <label key={t.id} data-testid={`kit-template-${t.id}`} className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer ${sel.includes(t.id) ? "border-indigo-500 bg-indigo-50/50" : "hover:bg-slate-50"}`}>
          <Checkbox checked={sel.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`kit-template-check-${t.id}`} className="mt-0.5" />
          <span><span className="block text-sm font-medium">{t.name}</span><span className="block text-[11px] text-slate-500">{t.description}</span></span>
        </label>))}
    </div>
  );
}

export function ContractCreateDialog({ open, onOpenChange, employee, onCreated }) {
  const [tpls, setTpls] = useState([]);
  const [sel, setSel] = useState([]);
  const [docs, setDocs] = useState([]);
  const [cur, setCur] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get("/contract-templates").then((r) => setTpls(r.data.templates)).catch((e) => toast.error(errMsg(e)));
    else { setSel([]); setDocs([]); setCur(0); }
  }, [open]);
  const toggle = async (id) => {
    const next = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    setSel(next);
    const kept = docs.filter((d) => next.includes(d.template_id));
    const missing = next.filter((x) => !kept.some((d) => d.template_id === x));
    let fresh = [];
    if (missing.length) { try { fresh = (await api.post("/contracts/preview", { employee_id: employee.id, template_ids: missing })).data.docs; } catch (e) { toast.error(errMsg(e)); return; } }
    const all = next.map((x) => kept.find((d) => d.template_id === x) || fresh.find((d) => d.template_id === x)).filter(Boolean);
    setDocs(all); setCur(Math.max(0, Math.min(cur, all.length - 1)));
  };
  const upd = (k, v) => setDocs(docs.map((d, i) => (i === cur ? { ...d, [k]: v } : d)));
  const create = async () => {
    setBusy(true);
    try { await api.post("/contracts", { employee_id: employee.id, docs }); toast.success("Enviado para assinatura. Compartilhe o link com o candidato."); onOpenChange(false); onCreated(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const d = docs[cur];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-create-dialog">
        <DialogHeader><DialogTitle>Kit de admissão para assinatura</DialogTitle>
          <DialogDescription>Escolha um ou mais documentos. {employee.name.split(" ")[0]} recebe um único link, aceita cada documento e assina uma vez só.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <TemplatePicker tpls={tpls} sel={sel} toggle={toggle} />
          {d && (<>
            <div className="flex flex-wrap gap-1.5 border-t pt-4">{docs.map((x, i) => (
              <button key={x.template_id} onClick={() => setCur(i)} data-testid={`kit-doc-tab-${i}`} className={`text-xs px-3 py-1.5 rounded-full border ${i === cur ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"}`}>
                {i + 1}. {x.title}{x.body.includes("________") && <AlertTriangle className="inline h-3 w-3 ml-1 text-amber-500" />}</button>))}</div>
            <div><Label>Título</Label><Input className="mt-1.5" data-testid="contract-title-input" value={d.title} onChange={(e) => upd("title", e.target.value)} /></div>
            <div><Label>Texto</Label><Textarea className="mt-1.5 min-h-[300px] text-xs leading-relaxed" data-testid="contract-body-input" value={d.body} onChange={(e) => upd("body", e.target.value)} /></div>
            {docs.some((x) => x.body.includes("________")) && <div data-testid="contract-blank-warning" className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"><AlertTriangle className="h-4 w-4 shrink-0" />Há campos sem informação no cadastro (________). Complete o cadastro ou edite o texto antes de enviar.</div>}
          </>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contract-create-cancel">Cancelar</Button>
          <Button disabled={!docs.length || docs.some((x) => !x.title.trim() || !x.body.trim()) || busy} onClick={create} data-testid="contract-create-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Enviar ${docs.length > 1 ? `${docs.length} documentos` : "documento"} para assinatura`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanySignForm({ contract, onClose, onSigned }) {
  const [sig, setSig] = useState(null);
  const [ok, setOk] = useState(false);
  const [position, setPosition] = useState("");
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    setBusy(true);
    try { await api.post(`/contracts/${contract.id}/sign-company`, { signature: sig, accept: ok, position }); toast.success("Assinado. PDFs salvos no Dossiê."); onClose(); onSigned(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (<>
    <DialogHeader><DialogTitle>Assinar como empresa</DialogTitle>
      <DialogDescription>{contract.employee_name} já assinou. Sua assinatura vale para todos os documentos abaixo e gera um PDF final para cada um, com manifesto de evidências.</DialogDescription></DialogHeader>
    <div className="space-y-4">
      <div className="space-y-1.5">{contract.docs.map((x, i) => (
        <button key={x.id} data-testid={`company-sign-read-${i}`} onClick={() => downloadFile(`/contracts/${contract.id}/download/${x.id}`, `${x.title}.pdf`)} className="w-full flex items-center gap-2 border rounded-lg px-3 py-2 text-sm text-left hover:bg-slate-50"><FileText className="h-4 w-4 text-indigo-600 shrink-0" />{x.title}<span className="ml-auto text-xs text-indigo-700">Ler PDF</span></button>))}</div>
      <div><Label>Seu cargo na empresa</Label><Input className="mt-1.5" placeholder="Ex.: Sócio-administrador" data-testid="company-sign-position" value={position} onChange={(e) => setPosition(e.target.value)} /></div>
      <SignaturePad onChange={setSig} testid="company-signature-pad" />
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="company-sign-accept" className="mt-0.5" />Li {contract.docs.length > 1 ? "todos os documentos" : "o documento"} e concordo em assinar eletronicamente em nome da empresa.</label>
    </div>
    <DialogFooter>
      <Button variant="outline" onClick={onClose}>Cancelar</Button>
      <Button disabled={!sig || !ok || busy} onClick={sign} data-testid="company-sign-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assinar"}</Button>
    </DialogFooter>
  </>);
}

export function CompanySignDialog({ contract, onOpenChange, onSigned }) {
  return (
    <Dialog open={!!contract} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="company-sign-dialog">
        {contract && <CompanySignForm key={contract.id} contract={contract} onClose={() => onOpenChange(false)} onSigned={onSigned} />}
      </DialogContent>
    </Dialog>
  );
}
